// client/src/pages/PeHealthReport.tsx
//
// PE Health — our own durable record of Practice Engine's Engine_BMSS, backed by
// api/pe-health (tables: PeHealth.*, migration 020). PE's own Query Store keeps
// filling its 100 MB cap and self-purging to zero retained intervals, so this is
// the only history we can rely on.
//
// The metric that matters is WorkerPct: Msg 10928 ("the request limit for the
// database is N and has been reached") fires on worker-thread exhaustion, and
// max_worker_percent is how close we are to that ceiling. Sessions were never the
// constraint — session_pct sat under 1% through every incident.
//
// Chart notes (see the dataviz method):
//   * Worker% and CPU% share one y-axis legitimately — both are percentages of
//     their own governed limit, 0-100. No dual-axis anywhere in this file.
//   * Series colors are categorical slots 1 and 2 from the validated dark
//     palette; the 75/90 thresholds are drawn as reference lines so a bar's
//     status color is never the only encoding.
//   * Every chart has a table beneath it carrying the same numbers.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, ComposedChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea, Cell,
} from 'recharts';
import {
  Activity, AlertTriangle, RefreshCw, Database, Clock, Cpu,
  ListChecks, GitCompare, Server, TriangleAlert, CheckCircle2, Workflow,
} from 'lucide-react';

// ---------- palette ----------
// validate_palette.js "#3987e5,#d95926,#9085e9" --mode dark --surface #121417
//   all six checks pass · worst adjacent CVD ΔE 26.0 deutan / 25.8 tritan
//
// The overlay was aqua (#199e70) first. Measured against the CPU orange that was
// the weakest pair in the set at ΔE 9.4 deutan — which is precisely why a thin
// green line disappeared into dense orange spikes. Violet measures 26.0. Note the
// fix was NOT to change the orange: swapping CPU to amber (#c98500) tested worse,
// dropping tritan separation to 17.3.
const SERIES = { worker: '#3987e5', cpu: '#d95926', runs: '#9085e9' };
const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' };
const GRID = 'rgba(255,255,255,.07)';

// Worker-percent thresholds. 90 is where Msg 10928 becomes likely; 75 is the
// "look at this before it bites" line.
const WARN_AT = 75;
const CRIT_AT = 90;

// ---------- types ----------
type Status = {
  ok: boolean;
  LatestSampleUtc?: string | null;
  SampleAgeMin?: number | null;
  TotalSamples?: number;
  DistinctQueries?: number;
  FailedRuns24h?: number;
  Runs24h?: number;
  retention?: Record<string, number>;
  currentConfig?: {
    CapturedUtc?: string; SloName?: string; CpuLimit?: number; MaxWorkers?: number;
    MaxDop?: string; OptimizeAdHoc?: string; ForceLastGoodPlan?: string;
    QueryStoreState?: string; SqlStartUtc?: string; ActiveRegressions?: number;
  } | null;
};
type DayRow = {
  DayUtc: string; Samples: number; CpuAvg: number | null; CpuPeak: number | null;
  WorkerPeak: number | null; SessionPeak: number | null; MemoryAvg: number | null;
  WindowsWorkerGe50: number; WindowsWorkerGe75: number; WindowsWorkerGe90: number; WindowsCpuGe90: number;
};
type SampleRow = {
  SampleUtc: string; CpuPct: number | null; DataIoPct: number | null;
  WorkerPct: number | null; SessionPct: number | null; MemoryPct: number | null;
};
type QueryRow = {
  QueryHash: string | null; Attribution: string | null; AttributionNote: string | null;
  TextChars: number | null; Snippet: string | null; Captures: number;
  CpuSecInWindow: number | null; ExecsInWindow: number | null;
  PeakCumulativeCpuSec: number | null; MaxDop: number | null; PeakLogicalReads: number | null;
  TimesCaughtRunning: number; WorstElapsedSec: number | null; LastSeenUtc: string | null; Resets: number;
};
type WaitRow = { DayUtc: string; WaitType: string; TasksDelta: number; WaitSecDelta: number; Restarts: number };
type OrphanRow = {
  DayUtc: string; LoginName: string; PeakOver1h: number; PeakOver24h: number;
  OldestMinutes: number | null; PeakStuckNoRead: number;
};
type DriftRow = {
  ConfigSnapshotId: number; CapturedUtc: string; SloName: string | null; CpuLimit: number | null;
  MaxWorkers: number | null; MaxDop: string | null; OptimizeAdHoc: string | null;
  ForceLastGoodPlan: string | null; QueryStoreState: string | null; SqlStartUtc: string | null;
  ActiveRegressions: number | null;
};
type RunRow = {
  StartedUtc: string; Mode: string; DurationMs: number | null; SamplesInserted: number | null;
  WaitsInserted: number | null; QueriesCaptured: number | null; ConfigChanged: boolean | null;
  RowsDeleted: number | null; Ok: boolean; Skipped: string | null; ErrorText: string | null;
  TriggeredBy: string | null;
};

type AlertRow = {
  AlertId: number; FiredUtc: string; RuleKey: string; Threshold: number;
  PeakValue: number | null; BreachSamples: number | null;
  WindowStartUtc: string | null; WindowEndUtc: string | null;
  CpuPeak: number | null; WorkerPeak: number | null; MaxWorkers: number | null;
  Recipients: string | null; Sent: boolean; ErrorText: string | null;
};

type RunBucket = {
  bucketUtc: string; runCount: number; failedCount: number;
  apps: { name: string; runs: number }[]; distinctApps: number;
};
type RunTimeline = {
  hours: number; bucketMin: number; errors: number;
  targetDatabase?: string; workflowsTotal?: number; workflowsScanned: number;
  budgetExhausted?: boolean; skippedForBudget?: number; truncatedApps?: number;
  totalRuns: number; peakRunCount: number; rows: RunBucket[];
  fromCache?: boolean; cacheAgeSec?: number;
};

type Tab = 'overview' | 'queries' | 'waits' | 'orphans' | 'drift' | 'collector';

// ---------- helpers ----------
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, { credentials: 'include', signal });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const b = await r.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return r.json();
}
const n1 = (v: number | null | undefined) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));
const int = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : Number(v).toLocaleString();
function agoMin(mins?: number | null) {
  if (mins === null || mins === undefined) return 'never';
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}
const workerColor = (v: number | null | undefined) => {
  if (v === null || v === undefined) return SERIES.worker;
  if (v >= CRIT_AT) return STATUS.critical;
  if (v >= WARN_AT) return STATUS.warning;
  return SERIES.worker;
};
// ---------- time handling ----------
//
// The API returns UTC as "YYYY-MM-DD HH:MM:SS" with no zone marker. Passing that
// straight to `new Date()` is a trap: it is not ISO-8601, so engines parse it as
// LOCAL time and every reading silently shifts by the UTC offset. Always add the
// 'T' and the 'Z' first.
const asDate = (s: string) => new Date(`${(s || '').replace(' ', 'T')}Z`);

// Whatever zone the browser is in, spelled the way a human would recognise it
// ("CDT"), so the axis is never ambiguous.
function localZoneLabel(): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || 'local';
  } catch {
    return 'local';
  }
}

type Tz = 'local' | 'utc';

// Local mode uses 12-hour with am/pm; UTC mode stays 24-hour.
//
// That split is deliberate rather than inconsistent. A bare 24-hour axis is only
// unambiguous for readers who already know it is 24-hour: "19:33" announces
// itself, but "06:46" and "10:01" read equally well as morning or evening, which
// is exactly the confusion this caused. Meanwhile UTC is conventionally written
// 24-hour, and keeping it that way makes the two modes visually distinct — you
// can tell at a glance which one you are looking at.
function makeTime(tz: Tz) {
  const zone = tz === 'utc' ? 'UTC' : localZoneLabel();
  const h12 = tz !== 'utc';
  return {
    zone,
    // chart ticks: "6:46 AM" local, "06:46" UTC
    hm: (s: string) =>
      tz === 'utc'
        ? (s || '').slice(11, 16)
        : asDate(s).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
    // full instant for tooltips, the config banner and the axis range. Always
    // carries the DATE: these windows routinely cross midnight, and a time alone
    // cannot say which side of it you are on.
    full: (s: string | null | undefined) => {
      if (!s) return '—';
      return tz === 'utc'
        ? `${s} UTC`
        : `${asDate(s).toLocaleString([], {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
          })} ${zone}`;
    },
    // short form for dense table columns
    short: (s: string | null | undefined) => {
      if (!s) return '—';
      return tz === 'utc'
        ? (s || '').slice(5, 16)
        : asDate(s).toLocaleString([], {
            month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: h12,
          });
    },
  };
}

// ---------- small UI bits ----------
function Kpi({ label, value, accent, icon, sub }: {
  label: string; value: React.ReactNode; accent?: string; icon?: React.ReactNode; sub?: string;
}) {
  return (
    <div className="card" style={{ padding: '12px 18px', minWidth: 140, textAlign: 'center', borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        {icon}
        <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: accent || 'var(--text)' }}>{value}</span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>{label}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2, opacity: .75 }}>{sub}</div>}
    </div>
  );
}

// Identity is carried by a swatch beside text-token text, never by coloring the text.
function Swatch({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text)' }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flex: '0 0 auto' }} />
      {children}
    </span>
  );
}

function AttrBadge({ attribution, note }: { attribution: string | null; note: string | null }) {
  const map: Record<string, { label: string; color: string }> = {
    bmss: { label: 'BMSS', color: SERIES.worker },
    pe: { label: 'PE', color: SERIES.cpu },
    unknown: { label: 'unknown', color: 'var(--muted)' },
  };
  const m = map[attribution || 'unknown'] || map.unknown;
  return (
    <span title={note || 'No marker matched — attribution is a heuristic, not a measurement.'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
        color: 'var(--text)', border: `1px solid ${m.color}`, padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap',
      }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color }} />
      {m.label}
    </span>
  );
}

function TabBar({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <Activity size={14} /> },
    { key: 'queries', label: 'Problem queries', icon: <ListChecks size={14} /> },
    { key: 'waits', label: 'Wait growth', icon: <Clock size={14} /> },
    { key: 'orphans', label: 'Orphan watch', icon: <TriangleAlert size={14} /> },
    { key: 'drift', label: 'Config drift', icon: <GitCompare size={14} /> },
    { key: 'collector', label: 'Collector', icon: <Server size={14} /> },
  ];
  return (
    <div className="row" style={{ gap: 6, marginBottom: 14 }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => setTab(t.key)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
            padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
            border: `1px solid ${tab === t.key ? SERIES.worker : 'var(--border)'}`,
            background: tab === t.key ? 'color-mix(in srgb, #3987e5 16%, transparent)' : 'transparent',
            color: 'var(--text)',
          }}>
          {t.icon}{t.label}
        </button>
      ))}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4,
  color: 'var(--muted)', padding: '8px 10px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  padding: '7px 10px', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,.05)', whiteSpace: 'nowrap',
};

function Empty({ what }: { what: string }) {
  return <div className="muted" style={{ padding: 18, fontSize: 13 }}>No {what} yet. The collector writes every 5 minutes once the scheduler is wired.</div>;
}

// Recharts tooltip, restyled so values wear text tokens rather than series colors.
// `labelText` comes off the hovered datum so the tooltip can show a full, zone-
// labelled instant while the axis tick stays a bare hh:mm.
function VizTooltip({ active, payload, label, unit = '%' }: any) {
  if (!active || !payload?.length) return null;
  const heading = payload[0]?.payload?.labelText || label;
  return (
    <div style={{
      background: '#0b0d10', border: '1px solid var(--border)', borderRadius: 8,
      padding: '8px 10px', fontSize: 12, color: 'var(--text)', boxShadow: '0 4px 14px rgba(0,0,0,.5)',
    }}>
      <div className="muted" style={{ marginBottom: 4 }}>{heading}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span>
            {p.name}:{' '}
            <strong>
              {p.value === null || p.value === undefined
                ? '—'
                : p.dataKey === 'runIdx'
                  ? `${payload[0]?.payload?.runCount ?? 0} runs`
                  : `${Number(p.value).toFixed(1)}${unit}`}
            </strong>
          </span>
        </div>
      ))}
      {payload[0]?.payload?.runApps?.length > 0 && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
          <div className="muted" style={{ fontSize: 11, marginBottom: 3 }}>
            ran in this 5-min bucket{payload[0].payload.runFailed ? ` · ${payload[0].payload.runFailed} failed` : ''}
          </div>
          {payload[0].payload.runApps.map((a: any) => (
            <div key={a.name} style={{ fontSize: 11.5 }}>
              {a.name} <span className="muted">×{a.runs}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
export default function PeHealthReport() {
  const [tab, setTab] = useState<Tab>('overview');
  const [days, setDays] = useState(7);
  const [hours, setHours] = useState(24);
  // Default to the reader's own clock — a dashboard is read by people, not by
  // the database. UTC stays one click away because the stored data and the
  // hour/day rollup buckets are UTC.
  const [tz, setTz] = useState<Tz>('local');
  const T = useMemo(() => makeTime(tz), [tz]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [status, setStatus] = useState<Status | null>(null);
  const [overview, setOverview] = useState<DayRow[]>([]);
  const [samples, setSamples] = useState<SampleRow[]>([]);
  const [queries, setQueries] = useState<QueryRow[]>([]);
  const [waits, setWaits] = useState<WaitRow[]>([]);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);
  const [drift, setDrift] = useState<DriftRow[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [showRuns, setShowRuns] = useState(false);
  const [timeline, setTimeline] = useState<RunTimeline | null>(null);
  const [tlBusy, setTlBusy] = useState(false);
  const [tlErr, setTlErr] = useState<string | null>(null);
  const [attrFilter, setAttrFilter] = useState<'' | 'bmss' | 'pe' | 'unknown'>('');
  const [openHash, setOpenHash] = useState<string | null>(null);
  const [fullText, setFullText] = useState<Record<string, string>>({});

  const load = useCallback(async (signal?: AbortSignal) => {
    setBusy(true); setErr(null);
    try {
      const [s, o, sm] = await Promise.all([
        getJson<Status>('/api/pe-health?mode=status', signal),
        getJson<{ rows: DayRow[] }>(`/api/pe-health?mode=overview&days=${days}`, signal),
        getJson<{ rows: SampleRow[] }>(`/api/pe-health?mode=samples&hours=${hours}`, signal),
      ]);
      setStatus(s); setOverview(o.rows || []); setSamples(sm.rows || []);
    } catch (e: any) {
      if (e.name !== 'AbortError') setErr(e.message || String(e));
    } finally { setBusy(false); }
  }, [days, hours]);

  useEffect(() => { const c = new AbortController(); load(c.signal); return () => c.abort(); }, [load]);

  // Logic App run overlay. Fetched only when switched on: it fans out one ARM
  // read per SQL-touching workflow, so it is far more expensive than anything
  // else on this page even with the endpoint's 5-minute cache.
  useEffect(() => {
    if (!showRuns) return;
    const c = new AbortController();
    setTlBusy(true); setTlErr(null);
    getJson<RunTimeline>(`/api/logicapp-health?mode=runtimeline&hours=${hours}&bucketMin=5`, c.signal)
      .then(setTimeline)
      .catch(e => { if (e.name !== 'AbortError') setTlErr(e.message || String(e)); })
      .finally(() => setTlBusy(false));
    return () => c.abort();
  }, [showRuns, hours]);

  // Lazy-load each tab's own dataset.
  useEffect(() => {
    const c = new AbortController();
    (async () => {
      try {
        if (tab === 'queries') {
          const q = await getJson<{ rows: QueryRow[] }>(
            `/api/pe-health?mode=queries&days=${days}&limit=30${attrFilter ? `&attribution=${attrFilter}` : ''}`, c.signal);
          setQueries(q.rows || []);
        } else if (tab === 'waits') {
          const w = await getJson<{ rows: WaitRow[] }>(`/api/pe-health?mode=waits&days=${days}`, c.signal);
          setWaits(w.rows || []);
        } else if (tab === 'orphans') {
          const o = await getJson<{ rows: OrphanRow[] }>(`/api/pe-health?mode=orphans&days=${days}`, c.signal);
          setOrphans(o.rows || []);
        } else if (tab === 'drift') {
          const d = await getJson<{ rows: DriftRow[] }>('/api/pe-health?mode=drift', c.signal);
          setDrift(d.rows || []);
        } else if (tab === 'collector') {
          const [r, a] = await Promise.all([
            getJson<{ rows: RunRow[] }>(`/api/pe-health?mode=collector&hours=${Math.max(hours, 24)}`, c.signal),
            getJson<{ rows: AlertRow[] }>('/api/pe-health?mode=alerts&hours=168', c.signal),
          ]);
          setRuns(r.rows || []);
          setAlerts(a.rows || []);
        }
      } catch (e: any) { if (e.name !== 'AbortError') setErr(e.message || String(e)); }
    })();
    return () => c.abort();
  }, [tab, days, hours, attrFilter]);

  async function toggleText(hash: string | null) {
    if (!hash) return;
    if (openHash === hash) { setOpenHash(null); return; }
    setOpenHash(hash);
    if (!fullText[hash]) {
      try {
        const r = await getJson<{ variants: { SqlText: string }[] }>(
          `/api/pe-health?mode=querytext&hash=${encodeURIComponent(hash)}`);
        setFullText(prev => ({ ...prev, [hash]: r.variants?.[0]?.SqlText || '(no text stored)' }));
      } catch (e: any) {
        setFullText(prev => ({ ...prev, [hash]: `Could not load text: ${e.message}` }));
      }
    }
  }

  // Charts read oldest→newest; the API returns samples ascending already.
  // Run activity is INDEXED to the window's own peak rather than given a second
  // y-axis. It answers the question that matters -- "where is activity spiking
  // relative to normal" -- while keeping one 0-100 axis, so a reader cannot
  // mistake a run count for a percentage. The raw count and the app names ride
  // along in the tooltip, which is where the absolute numbers are legible anyway.
  const runIndex = useMemo(() => {
    const m = new Map<string, { idx: number; count: number; apps: { name: string; runs: number }[]; failed: number }>();
    if (!timeline?.rows?.length) return m;
    const peak = Math.max(1, timeline.peakRunCount);
    for (const r of timeline.rows) {
      m.set(r.bucketUtc, {
        idx: Math.round((r.runCount / peak) * 1000) / 10,
        count: r.runCount,
        apps: r.apps || [],
        failed: r.failedCount || 0,
      });
    }
    return m;
  }, [timeline]);

  // Which run bucket a 15-second sample falls into.
  // Built from UTC parts rather than by string-munging an ISO timestamp.
  //
  // The previous version did .replace(/.d+Z$/, '') — the backslashes had been
  // eaten before it reached the file, so the pattern meant "any char, then one or
  // more literal d, then Z" and never stripped the milliseconds. The key came out
  // "2026-08-18 14:05:00.000" against the server's "2026-08-18 14:05:00", every
  // lookup missed, and the overlay drew a flat line at zero while the API was
  // returning 2,203 runs. Arithmetic cannot fail that way.
  const bucketKeyFor = useCallback((sampleUtc: string) => {
    const bucketMin = timeline?.bucketMin || 5;
    const t = asDate(sampleUtc).getTime();
    if (!Number.isFinite(t)) return null;
    const ms = bucketMin * 60 * 1000;
    const d = new Date(Math.floor(t / ms) * ms);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
           `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
  }, [timeline]);

  // ---------- zoom ----------
  //
  // Index-based windowing over the sample array rather than a numeric x-domain:
  // the x axis is a category axis of formatted labels, and those labels repeat
  // (hh:mm recurs across days), so a value-based domain would be ambiguous.
  // Recharts hands us activeTooltipIndex on mouse events, which is not.
  const [zoom, setZoom] = useState<{ start: number; end: number } | null>(null);
  const [dragFrom, setDragFrom] = useState<string | null>(null);
  const [dragTo, setDragTo] = useState<string | null>(null);
  const dragIdx = useRef<{ from: number | null; to: number | null }>({ from: null, to: null });
  const chartBox = useRef<HTMLDivElement | null>(null);
  const MIN_POINTS = 8; // below this the chart stops being readable

  const chartSamples = useMemo(
    () => samples.map(s => {
      const key = showRuns ? bucketKeyFor(s.SampleUtc) : null;
      const hit = key ? runIndex.get(key) : undefined;
      return {
        t: T.hm(s.SampleUtc),
        labelText: T.full(s.SampleUtc),
        worker: s.WorkerPct,
        cpu: s.CpuPct,
        runIdx: showRuns ? (hit ? hit.idx : 0) : undefined,
        runCount: hit ? hit.count : undefined,
        runApps: hit ? hit.apps : undefined,
        runFailed: hit ? hit.failed : undefined,
      };
    }),
    [samples, T, showRuns, runIndex, bucketKeyFor]
  );
  const chartDays = useMemo(
    () => overview.slice().reverse().map(d => ({ d: d.DayUtc.slice(5), workerPeak: d.WorkerPeak, cpuPeak: d.CpuPeak })),
    [overview]
  );
  // Spelled out with dates in the chart subtitle. Recharts thins ticks by
  // minTickGap, so a date attached to one datum may never be chosen as a tick —
  // the range in the header is the reliable place to say which day is which.
  const sampleRange = useMemo(() => {
    if (!samples.length) return null;
    return { from: T.full(samples[0].SampleUtc), to: T.full(samples[samples.length - 1].SampleUtc) };
  }, [samples, T]);

  // The slice actually rendered. Everything below works in absolute indices into
  // chartSamples so nesting a zoom inside a zoom stays correct.
  const visibleSamples = useMemo(
    () => (zoom ? chartSamples.slice(zoom.start, zoom.end) : chartSamples),
    [chartSamples, zoom]
  );
  const zoomRange = useMemo(() => {
    if (!zoom || !samples.length) return null;
    const a = samples[Math.min(zoom.start, samples.length - 1)];
    const b = samples[Math.min(zoom.end - 1, samples.length - 1)];
    if (!a || !b) return null;
    return { from: T.full(a.SampleUtc), to: T.full(b.SampleUtc), count: zoom.end - zoom.start };
  }, [zoom, samples, T]);

  const resetZoom = useCallback(() => {
    setZoom(null);
    setDragFrom(null); setDragTo(null);
    dragIdx.current = { from: null, to: null };
  }, []);

  // Drag horizontally to select a range; release to zoom into it.
  const onChartMouseDown = useCallback((e: any) => {
    if (!e || e.activeTooltipIndex == null) return;
    dragIdx.current = { from: e.activeTooltipIndex, to: null };
    setDragFrom(e.activeLabel ?? null);
    setDragTo(null);
  }, []);
  const onChartMouseMove = useCallback((e: any) => {
    if (dragIdx.current.from == null || !e || e.activeTooltipIndex == null) return;
    dragIdx.current.to = e.activeTooltipIndex;
    setDragTo(e.activeLabel ?? null);
  }, []);
  const onChartMouseUp = useCallback(() => {
    const { from, to } = dragIdx.current;
    dragIdx.current = { from: null, to: null };
    setDragFrom(null); setDragTo(null);
    if (from == null || to == null) return;
    const base = zoom ? zoom.start : 0;
    let lo = base + Math.min(from, to);
    let hi = base + Math.max(from, to) + 1;
    // A click (or a nudge) is not a zoom request — ignore it rather than
    // collapsing the chart to a couple of points.
    if (hi - lo < MIN_POINTS) return;
    setZoom({ start: lo, end: hi });
  }, [zoom]);

  // Wheel zoom, anchored on the cursor.
  //
  // Attached as a NATIVE non-passive listener rather than via onWheel: React
  // registers wheel handlers passively, so preventDefault() inside onWheel is
  // ignored and the page scrolls behind the chart while you try to zoom it.
  useEffect(() => {
    const el = chartBox.current;
    if (!el) return;
    const onWheel = (ev: WheelEvent) => {
      const total = chartSamples.length;
      if (total < MIN_POINTS) return;
      ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const ratio = rect.width > 0 ? Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width)) : 0.5;
      setZoom((cur) => {
        const start = cur ? cur.start : 0;
        const end = cur ? cur.end : total;
        const span = end - start;
        const factor = ev.deltaY > 0 ? 1.25 : 0.8;          // down = out, up = in
        let newSpan = Math.round(span * factor);
        newSpan = Math.max(MIN_POINTS, Math.min(total, newSpan));
        if (newSpan === span) return cur;
        if (newSpan >= total) return null;                   // fully zoomed out
        const anchor = start + span * ratio;                 // keep this point put
        let newStart = Math.round(anchor - newSpan * ratio);
        newStart = Math.max(0, Math.min(total - newSpan, newStart));
        return { start: newStart, end: newStart + newSpan };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [chartSamples.length]);

  // A new fetch changes the array under us; an old index window would frame the
  // wrong slice.
  useEffect(() => { setZoom(null); }, [hours, samples.length]);

  const today = overview[0];
  const cfg = status?.currentConfig;
  const stale = (status?.SampleAgeMin ?? 9999) > 20;
  const windowWorkerPeak = useMemo(
    () => samples.reduce((m, s) => Math.max(m, s.WorkerPct ?? 0), 0),
    [samples]
  );
  const nearCeiling = useMemo(
    () => overview.reduce((a, d) => a + (d.WindowsWorkerGe75 || 0), 0),
    [overview]
  );

  return (
    <div style={{ padding: '18px 22px 40px' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 22, margin: 0 }}>
          <Database size={20} /> PE Health
        </h1>
        <div className="row" style={{ gap: 8 }}>
          <label className="muted" style={{ fontSize: 12 }}>
            Window{' '}
            <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))}
              style={{ background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 6px' }}>
              {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>{d}d</option>)}
            </select>
          </label>
          <label className="muted" style={{ fontSize: 12 }}>
            Detail{' '}
            <select value={hours} onChange={e => setHours(parseInt(e.target.value, 10))}
              style={{ background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 6px' }}>
              {[6, 12, 24, 48, 72].map(h => <option key={h} value={h}>{h}h</option>)}
            </select>
          </label>
          <button onClick={() => setTz(tz === 'local' ? 'utc' : 'local')}
            title={tz === 'local'
              ? `Showing your local clock (${T.zone}). Click for UTC.`
              : 'Showing UTC. Click for your local clock.'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${SERIES.worker}`, background: 'transparent', color: 'var(--text)' }}>
            <Clock size={14} /> {T.zone}
          </button>
          <button onClick={() => load()} disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)' }}>
            <RefreshCw size={14} style={{ animation: busy ? 'spin 1s linear infinite' : undefined }} /> Refresh
          </button>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: '0 0 14px', maxWidth: 940 }}>
        Our own record of Engine_BMSS, collected every 5 minutes. <strong>Worker %</strong> is the metric
        that matters — <code>Msg 10928</code> fires on worker-thread exhaustion, not on session count.
        Timestamps are shown in <strong>{T.zone}</strong> (click the clock to switch); daily and hourly
        rollups are always bucketed on UTC boundaries and labelled as such.
      </p>

      {err && (
        <div className="card" style={{ padding: 12, marginBottom: 14, borderLeft: `3px solid ${STATUS.critical}`, fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{err}
        </div>
      )}

      {stale && status && (
        <div className="card" style={{ padding: 12, marginBottom: 14, borderLeft: `3px solid ${STATUS.warning}`, fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          Newest sample is <strong>{agoMin(status.SampleAgeMin)}</strong>. A stale feed usually means the
          collector is failing, not that PE is quiet — check the Collector tab.
        </div>
      )}

      {/* ---------- KPI row ---------- */}
      <div className="row" style={{ gap: 10, marginBottom: 16 }}>
        <Kpi label={`Worker peak (${hours}h)`} value={`${n1(windowWorkerPeak)}%`}
          accent={workerColor(windowWorkerPeak)} icon={<Cpu size={16} />}
          sub={cfg?.MaxWorkers ? `ceiling ${int(cfg.MaxWorkers)} workers` : undefined} />
        <Kpi label="CPU avg today" value={`${n1(today?.CpuAvg)}%`} icon={<Activity size={16} />} />
        <Kpi label={`Windows ≥${WARN_AT}% worker (${days}d)`} value={int(nearCeiling)}
          accent={nearCeiling > 0 ? STATUS.warning : STATUS.good} />
        <Kpi label="Plan regressions" value={int(cfg?.ActiveRegressions)}
          accent={(cfg?.ActiveRegressions ?? 0) > 0 ? STATUS.serious : STATUS.good}
          sub={cfg?.ForceLastGoodPlan === 'OFF' ? 'auto-fix is OFF' : undefined} />
        <Kpi label="Collector" value={status?.FailedRuns24h ? `${status.FailedRuns24h} failed` : 'healthy'}
          accent={status?.FailedRuns24h ? STATUS.critical : STATUS.good}
          icon={status?.FailedRuns24h ? <TriangleAlert size={16} /> : <CheckCircle2 size={16} />}
          sub={`${int(status?.Runs24h)} runs / 24h`} />
      </div>

      {/* ---------- current config banner ---------- */}
      {cfg && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12.5, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span className="muted">Tier <strong style={{ color: 'var(--text)' }}>{cfg.CpuLimit} vCore</strong></span>
          <span className="muted">Worker ceiling <strong style={{ color: 'var(--text)' }}>{int(cfg.MaxWorkers)}</strong></span>
          <span className="muted">MAXDOP <strong style={{ color: cfg.MaxDop === '0' ? STATUS.warning : 'var(--text)' }}>{cfg.MaxDop ?? '—'}</strong>{cfg.MaxDop === '0' ? ' (unlimited)' : ''}</span>
          <span className="muted">FORCE_LAST_GOOD_PLAN <strong style={{ color: cfg.ForceLastGoodPlan === 'OFF' ? STATUS.warning : 'var(--text)' }}>{cfg.ForceLastGoodPlan ?? '—'}</strong></span>
          <span className="muted">Query Store <strong style={{ color: cfg.QueryStoreState === 'READ_ONLY' ? STATUS.warning : 'var(--text)' }}>{cfg.QueryStoreState ?? '—'}</strong></span>
          <span className="muted">SQL up since <strong style={{ color: 'var(--text)' }}>{T.full(cfg.SqlStartUtc)}</strong></span>
        </div>
      )}

      <TabBar tab={tab} setTab={setTab} />

      {/* ================= OVERVIEW ================= */}
      {tab === 'overview' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
              Worker and CPU utilisation — last {hours}h ({T.zone})
              <span className="muted" style={{ fontWeight: 400 }}> · 15-second samples · both axes are percent of their own governed limit</span>
              {sampleRange && (
                <div className="muted" style={{ fontWeight: 400, fontSize: 11.5, marginTop: 3 }}>
                  {zoomRange ? (
                    <>
                      <strong style={{ color: SERIES.worker }}>{zoomRange.from} → {zoomRange.to}</strong>
                      {' '}· {int(zoomRange.count)} samples zoomed
                      <button onClick={resetZoom}
                        style={{
                          marginLeft: 8, fontSize: 11, padding: '1px 7px', borderRadius: 6, cursor: 'pointer',
                          border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
                        }}>reset</button>
                      <span style={{ marginLeft: 8, opacity: .7 }}>of {sampleRange.from} → {sampleRange.to}</span>
                    </>
                  ) : (
                    <>
                      {sampleRange.from} → {sampleRange.to}
                      <span style={{ marginLeft: 10, opacity: .7 }}>drag to zoom · scroll at cursor · double-click resets</span>
                    </>
                  )}
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => setShowRuns(!showRuns)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                    padding: '4px 10px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${showRuns ? SERIES.runs : 'var(--border)'}`,
                    background: showRuns ? 'color-mix(in srgb, #199e70 16%, transparent)' : 'transparent',
                    color: 'var(--text)',
                  }}>
                  <Workflow size={13} /> {showRuns ? 'Hide' : 'Overlay'} Logic App runs
                </button>
                {showRuns && tlBusy && <span className="muted" style={{ fontSize: 11.5 }}>loading run history…</span>}
                {showRuns && tlErr && (
                  <span style={{ fontSize: 11.5, color: STATUS.critical }}>{tlErr}</span>
                )}
                {showRuns && timeline && !tlBusy && (
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    {int(timeline.totalRuns)} runs across {timeline.workflowsScanned}
                    {timeline.workflowsTotal ? `/${timeline.workflowsTotal}` : ''} workflows targeting{' '}
                    {timeline.targetDatabase || 'Engine_BMSS'} · peak {timeline.peakRunCount}/5min = 100% on this axis
                    {timeline.errors ? ` · ${timeline.errors} unreadable` : ''}
                    {timeline.fromCache ? ` · cached ${timeline.cacheAgeSec}s` : ''}
                  </span>
                )}
              </div>
              {showRuns && timeline && (timeline.budgetExhausted || timeline.truncatedApps) && (
                <div style={{ fontSize: 11.5, marginTop: 6, maxWidth: 880, color: STATUS.warning }}>
                  <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                  <strong>Partial coverage.</strong>{' '}
                  {timeline.budgetExhausted
                    ? `${timeline.skippedForBudget} workflow(s) were skipped to stay inside the request time budget. `
                    : ''}
                  {timeline.truncatedApps
                    ? `${timeline.truncatedApps} workflow(s) had more runs than one page holds, so the earliest part of the window is thin. `
                    : ''}
                  Narrow the Detail window for full coverage.
                </div>
              )}
              {showRuns && (
                <div className="muted" style={{ fontWeight: 400, fontSize: 11, marginTop: 5, maxWidth: 880 }}>
                  <strong>Coincidence, not causation.</strong> Run activity is indexed to its own peak so the
                  shape is comparable on one axis — it is not a percentage of anything. With a 5-minute tick and
                  this many workflows most spikes overlap several runs, so use this to narrow the search, then
                  confirm on the <em>Problem queries</em> tab, where a live capture names the actual statement.
                </div>
              )}
            </div>
            <div
              ref={chartBox}
              className="bd"
              style={{ height: 260, cursor: dragFrom !== null ? 'ew-resize' : 'crosshair', userSelect: 'none' }}
              onDoubleClick={resetZoom}
              title="Drag to zoom · scroll to zoom at the cursor · double-click to reset"
            >
              {visibleSamples.length === 0 ? <Empty what="samples" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  {/* ComposedChart, not LineChart: the run overlay is drawn as an
                      AREA behind the two metric lines. Separating it by FORM does
                      what no hue swap can — it stops competing as a line and reads
                      as background context, which is what it is. Run counts are
                      not a percentage of anything. */}
                  <ComposedChart
                    data={visibleSamples}
                    margin={{ top: 6, right: 12, bottom: 0, left: -18 }}
                    onMouseDown={onChartMouseDown}
                    onMouseMove={onChartMouseMove}
                    onMouseUp={onChartMouseUp}
                  >
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="t" tick={{ fill: 'var(--muted)', fontSize: 11 }} stroke={GRID}
                      minTickGap={44} />
                    <YAxis domain={[0, 100]} tick={{ fill: 'var(--muted)', fontSize: 11 }} stroke={GRID}
                      tickFormatter={(v) => `${v}%`} />
                    <ReferenceLine y={CRIT_AT} stroke={STATUS.critical} strokeDasharray="4 4"
                      label={{ value: `${CRIT_AT}% throttle risk`, fill: 'var(--muted)', fontSize: 10, position: 'insideTopRight' }} />
                    <ReferenceLine y={WARN_AT} stroke={STATUS.warning} strokeDasharray="4 4" />
                    <Tooltip content={<VizTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12, color: 'var(--muted)' }} />
                    {showRuns && (
                      // First child = drawn first = behind. stepAfter because a
                      // bucket is a flat 5-minute count; smoothing would invent
                      // variation the data does not have.
                      <Area type="stepAfter" dataKey="runIdx" name="Logic App runs (indexed)"
                        stroke={SERIES.runs} strokeWidth={1.5} fill={SERIES.runs} fillOpacity={0.22}
                        dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                    )}
                    <Line type="monotone" dataKey="worker" name="Worker %" stroke={SERIES.worker}
                      strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                    <Line type="monotone" dataKey="cpu" name="CPU %" stroke={SERIES.cpu}
                      strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                    {dragFrom !== null && dragTo !== null && (
                      <ReferenceArea x1={dragFrom} x2={dragTo} strokeOpacity={0}
                        fill={SERIES.worker} fillOpacity={0.15} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
              Daily worker peak (UTC days)
              <span className="muted" style={{ fontWeight: 400 }}> · bars past a threshold take its status colour; the dashed lines carry the same reading</span>
            </div>
            <div className="bd" style={{ height: 200 }}>
              {chartDays.length === 0 ? <Empty what="daily rollups" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartDays} margin={{ top: 6, right: 12, bottom: 0, left: -18 }}>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="d" tick={{ fill: 'var(--muted)', fontSize: 11 }} stroke={GRID} />
                    <YAxis domain={[0, 100]} tick={{ fill: 'var(--muted)', fontSize: 11 }} stroke={GRID}
                      tickFormatter={(v) => `${v}%`} />
                    <ReferenceLine y={CRIT_AT} stroke={STATUS.critical} strokeDasharray="4 4" />
                    <ReferenceLine y={WARN_AT} stroke={STATUS.warning} strokeDasharray="4 4" />
                    <Tooltip content={<VizTooltip />} />
                    <Bar dataKey="workerPeak" name="Worker peak %" radius={[4, 4, 0, 0]} maxBarSize={38}>
                      {chartDays.map((d, i) => <Cell key={i} fill={workerColor(d.workerPeak)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="card">
            <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>Daily detail</div>
            <div style={{ overflowX: 'auto' }}>
              {overview.length === 0 ? <Empty what="daily rollups" /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Day (UTC)</th><th style={th}>Samples</th>
                    <th style={th}>CPU avg</th><th style={th}>CPU peak</th>
                    <th style={th}>Worker peak</th><th style={th}>Session peak</th><th style={th}>Mem avg</th>
                    <th style={th}>≥50%</th><th style={th}>≥75%</th><th style={th}>≥90%</th><th style={th}>CPU ≥90%</th>
                  </tr></thead>
                  <tbody>
                    {overview.map(d => (
                      <tr key={d.DayUtc}>
                        <td style={td}>{d.DayUtc}</td>
                        <td style={td}>{int(d.Samples)}</td>
                        <td style={td}>{n1(d.CpuAvg)}%</td>
                        <td style={td}>{n1(d.CpuPeak)}%</td>
                        <td style={{ ...td, fontWeight: 700 }}>
                          <Swatch color={workerColor(d.WorkerPeak)}>{n1(d.WorkerPeak)}%</Swatch>
                        </td>
                        <td style={td}>{n1(d.SessionPeak)}%</td>
                        <td style={td}>{n1(d.MemoryAvg)}%</td>
                        <td style={td}>{int(d.WindowsWorkerGe50)}</td>
                        <td style={{ ...td, color: d.WindowsWorkerGe75 ? 'var(--text)' : 'var(--muted)' }}>{int(d.WindowsWorkerGe75)}</td>
                        <td style={{ ...td, fontWeight: d.WindowsWorkerGe90 ? 700 : 400 }}>{int(d.WindowsWorkerGe90)}</td>
                        <td style={td}>{int(d.WindowsCpuGe90)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ================= QUERIES ================= */}
      {tab === 'queries' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>
              Worst queries · last {days}d
              <span className="muted" style={{ fontWeight: 400 }}> · ranked by CPU actually burned in the window</span>
            </span>
            <span className="row" style={{ gap: 6 }}>
              {(['', 'bmss', 'pe', 'unknown'] as const).map(a => (
                <button key={a || 'all'} onClick={() => setAttrFilter(a)}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${attrFilter === a ? SERIES.worker : 'var(--border)'}`,
                    background: 'transparent', color: 'var(--text)',
                  }}>{a === '' ? 'All' : a === 'bmss' ? 'BMSS' : a === 'pe' ? 'PE' : 'Unknown'}</button>
              ))}
            </span>
          </div>
          <div className="bd" style={{ paddingBottom: 0 }}>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
              <strong>Attribution is a heuristic</strong> from SQL-text markers — Engine_BMSS cannot tell us
              which application issued a statement. A query first seen in this window reads 0 CPU until its
              second capture, because the figure is movement between captures, not a cumulative counter.
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {queries.length === 0 ? <Empty what="query captures" /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Source</th><th style={th}>CPU sec</th><th style={th}>Execs</th>
                  <th style={th}>Max DOP</th><th style={th}>Peak reads</th><th style={th}>Caught running</th>
                  <th style={th}>Worst elapsed</th><th style={th}>Last seen</th><th style={th}>Query</th>
                </tr></thead>
                <tbody>
                  {queries.map(q => (
                    <React.Fragment key={q.QueryHash || Math.random()}>
                      <tr onClick={() => toggleText(q.QueryHash)} style={{ cursor: q.QueryHash ? 'pointer' : 'default' }}>
                        <td style={td}><AttrBadge attribution={q.Attribution} note={q.AttributionNote} /></td>
                        <td style={{ ...td, fontWeight: 700 }}>{n1(q.CpuSecInWindow)}</td>
                        <td style={td}>{int(q.ExecsInWindow)}</td>
                        <td style={{ ...td, color: (q.MaxDop ?? 0) > 1 ? STATUS.warning : 'var(--muted)' }}>{int(q.MaxDop)}</td>
                        <td style={td}>{int(q.PeakLogicalReads)}</td>
                        <td style={{ ...td, fontWeight: q.TimesCaughtRunning ? 700 : 400 }}>{int(q.TimesCaughtRunning)}</td>
                        <td style={td}>{q.WorstElapsedSec ? `${int(q.WorstElapsedSec)}s` : '—'}</td>
                        <td style={{ ...td, color: 'var(--muted)' }}>{T.short(q.LastSeenUtc)}</td>
                        <td style={{ ...td, whiteSpace: 'normal', maxWidth: 420, fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: 'var(--muted)' }}>
                          {q.Snippet || '—'}
                        </td>
                      </tr>
                      {openHash === q.QueryHash && (
                        <tr><td colSpan={9} style={{ ...td, whiteSpace: 'pre-wrap', background: 'rgba(0,0,0,.25)', fontFamily: 'ui-monospace, monospace', fontSize: 11.5, maxWidth: 0 }}>
                          <div className="muted" style={{ marginBottom: 6, fontFamily: 'inherit' }}>
                            {q.QueryHash} · {int(q.TextChars)} chars · {q.Captures} captures
                            {q.Resets ? ` · ${q.Resets} plan resets` : ''}
                          </div>
                          {fullText[q.QueryHash!] ?? 'Loading…'}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ================= WAITS ================= */}
      {tab === 'waits' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Wait growth per day · last {days}d
            <span className="muted" style={{ fontWeight: 400 }}> · THREADPOOL is the direct worker-exhaustion counter</span>
          </div>
          <div className="bd" style={{ paddingBottom: 0 }}>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
              These are deltas between captures. Intervals spanning a database restart are excluded —
              <code> sys.dm_os_wait_stats</code> resets there, and Engine_BMSS is serverless so it restarts often.
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {waits.length === 0 ? <Empty what="wait snapshots" /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Day (UTC)</th><th style={th}>Wait type</th>
                  <th style={th}>Tasks</th><th style={th}>Wait sec</th><th style={th}>Restarts</th>
                </tr></thead>
                <tbody>
                  {waits.map((w, i) => (
                    <tr key={`${w.DayUtc}-${w.WaitType}-${i}`}>
                      <td style={td}>{w.DayUtc}</td>
                      <td style={{ ...td, fontWeight: w.WaitType === 'THREADPOOL' ? 700 : 400 }}>{w.WaitType}</td>
                      <td style={td}>{int(w.TasksDelta)}</td>
                      <td style={td}>{int(w.WaitSecDelta)}</td>
                      <td style={{ ...td, color: w.Restarts ? STATUS.warning : 'var(--muted)' }}>{int(w.Restarts)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ================= ORPHANS ================= */}
      {tab === 'orphans' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Long-lived sessions by login · last {days}d
          </div>
          <div className="bd" style={{ paddingBottom: 0 }}>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
              <strong>Stuck (no read)</strong> counts sessions on <code>ASYNC_NETWORK_IO</code> — a finished query
              whose results nobody is collecting. Non-zero means the Logic App connector-abandonment pattern is
              back; those sessions each hold a worker until someone kills them, and we cannot (no
              <code> ALTER ANY CONNECTION</code>).
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {orphans.length === 0 ? <Empty what="long-lived sessions" /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Day (UTC)</th><th style={th}>Login</th>
                  <th style={th}>Peak &gt;1h</th><th style={th}>Peak &gt;24h</th>
                  <th style={th}>Oldest</th><th style={th}>Stuck (no read)</th>
                </tr></thead>
                <tbody>
                  {orphans.map((o, i) => (
                    <tr key={`${o.DayUtc}-${o.LoginName}-${i}`}>
                      <td style={td}>{o.DayUtc}</td>
                      <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>{o.LoginName}</td>
                      <td style={td}>{int(o.PeakOver1h)}</td>
                      <td style={{ ...td, fontWeight: o.PeakOver24h ? 700 : 400 }}>{int(o.PeakOver24h)}</td>
                      <td style={td}>{o.OldestMinutes ? `${Math.floor(o.OldestMinutes / 60)}h` : '—'}</td>
                      <td style={{ ...td, fontWeight: o.PeakStuckNoRead ? 700 : 400 }}>
                        {o.PeakStuckNoRead ? <Swatch color={STATUS.critical}>{int(o.PeakStuckNoRead)}</Swatch> : '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ================= DRIFT ================= */}
      {tab === 'drift' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Vendor-side configuration changes
            <span className="muted" style={{ fontWeight: 400 }}> · one row per actual change</span>
          </div>
          <div className="bd" style={{ paddingBottom: 0 }}>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
              PE scaled this database from 4 to 6 vCores (worker ceiling 400 → 600) on 2026-08-17 and has
              wiped Query Store twice, in both cases without telling us the specifics. This is the dated record.
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {drift.length === 0 ? <Empty what="config snapshots" /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Detected ({T.zone})</th><th style={th}>vCore</th><th style={th}>Workers</th>
                  <th style={th}>MAXDOP</th><th style={th}>Ad-hoc opt</th><th style={th}>FLGP</th>
                  <th style={th}>Query Store</th><th style={th}>SQL start</th><th style={th}>Regressions</th>
                  <th style={th}>SLO</th>
                </tr></thead>
                <tbody>
                  {drift.map(d => (
                    <tr key={d.ConfigSnapshotId}>
                      <td style={td}>{T.short(d.CapturedUtc)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{int(d.CpuLimit)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{int(d.MaxWorkers)}</td>
                      <td style={td}>{d.MaxDop ?? '—'}</td>
                      <td style={td}>{d.OptimizeAdHoc ?? '—'}</td>
                      <td style={td}>{d.ForceLastGoodPlan ?? '—'}</td>
                      <td style={td}>{d.QueryStoreState ?? '—'}</td>
                      <td style={{ ...td, color: 'var(--muted)' }}>{T.short(d.SqlStartUtc)}</td>
                      <td style={td}>{int(d.ActiveRegressions)}</td>
                      <td style={{ ...td, color: 'var(--muted)', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{d.SloName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ================= COLLECTOR ================= */}
      {tab === 'collector' && (
        <>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Threshold alerts · last 7 days
            <span className="muted" style={{ fontWeight: 400 }}> · sustained breaches pushed to Zoom</span>
          </div>
          <div className="bd" style={{ paddingBottom: 0 }}>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
              A rule fires only when the breach is <strong>sustained</strong> — consecutive 15-second samples
              over the line — and then not again until its cooldown expires. Measured against the first
              1,140 samples, CPU crossed 75% exactly once and that breach lasted a single 15-second sample;
              a bare threshold would have messaged four people about fifteen seconds of CPU.
              <strong> An empty table is only good news if the collector is also healthy.</strong>
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {alerts.length === 0 ? (
              <div className="muted" style={{ padding: 18, fontSize: 13 }}>No threshold alerts in the last 7 days.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Fired ({T.zone})</th><th style={th}>Rule</th><th style={th}>Peak</th>
                  <th style={th}>Threshold</th><th style={th}>Sustained</th><th style={th}>Delivered</th>
                  <th style={th}>Recipients</th><th style={th}>Error</th>
                </tr></thead>
                <tbody>
                  {alerts.map(a => (
                    <tr key={a.AlertId}>
                      <td style={td}>{T.short(a.FiredUtc)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>
                        <Swatch color={a.RuleKey === 'worker' ? STATUS.critical : STATUS.warning}>{a.RuleKey}</Swatch>
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{n1(a.PeakValue)}%</td>
                      <td style={td}>{n1(a.Threshold)}%</td>
                      <td style={td}>{a.BreachSamples ? `${a.BreachSamples * 15}s` : '—'}</td>
                      <td style={td}>
                        <Swatch color={a.Sent ? STATUS.good : STATUS.critical}>{a.Sent ? 'sent' : 'FAILED'}</Swatch>
                      </td>
                      <td style={{ ...td, color: 'var(--muted)', fontSize: 11.5 }}>
                        {(a.Recipients || '—').split(',').length} recipient(s)
                      </td>
                      <td style={{ ...td, whiteSpace: 'normal', maxWidth: 300, color: 'var(--muted)', fontSize: 12 }}>
                        {a.ErrorText || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Collector runs
            <span className="muted" style={{ fontWeight: 400 }}> · a silently failing collector looks the same as a quiet database, so it gets its own log</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {runs.length === 0 ? <Empty what="collector runs" /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Started ({T.zone})</th><th style={th}>Mode</th><th style={th}>OK</th>
                  <th style={th}>ms</th><th style={th}>Samples</th><th style={th}>Waits</th>
                  <th style={th}>Queries</th><th style={th}>Deleted</th><th style={th}>By</th><th style={th}>Note</th>
                </tr></thead>
                <tbody>
                  {runs.map((r, i) => (
                    <tr key={`${r.StartedUtc}-${i}`}>
                      <td style={td}>{T.short(r.StartedUtc)}</td>
                      <td style={td}>{r.Mode}</td>
                      <td style={td}><Swatch color={r.Ok ? STATUS.good : STATUS.critical}>{r.Ok ? 'ok' : 'fail'}</Swatch></td>
                      <td style={td}>{int(r.DurationMs)}</td>
                      <td style={td}>{int(r.SamplesInserted)}</td>
                      <td style={td}>{int(r.WaitsInserted)}</td>
                      <td style={td}>{int(r.QueriesCaptured)}</td>
                      <td style={td}>{int(r.RowsDeleted)}</td>
                      <td style={{ ...td, color: 'var(--muted)' }}>{r.TriggeredBy ?? '—'}</td>
                      <td style={{ ...td, whiteSpace: 'normal', maxWidth: 340, color: 'var(--muted)', fontSize: 12 }}>
                        {r.ErrorText || r.Skipped || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
